import React, { useState, useEffect, useCallback } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Grid,
  CircularProgress,
  Alert,
  InputAdornment,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import PriceChangeIcon from "@mui/icons-material/PriceChange"
import PercentIcon from "@mui/icons-material/Percent"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import ReportProblemIcon from "@mui/icons-material/ReportProblem"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"

// ----- Transfer List -----
function TransferList({ disponibles, seleccionados, onChange }) {
  const handleMoveRight = (item) => {
    onChange(disponibles.filter((a) => a.id !== item.id), [...seleccionados, item])
  }

  const handleMoveLeft = (item) => {
    onChange([...disponibles, item], seleccionados.filter((a) => a.id !== item.id))
  }

  const handleAllRight = () => {
    onChange([], [...seleccionados, ...disponibles])
  }

  const handleAllLeft = () => {
    onChange([...disponibles, ...seleccionados], [])
  }

  const customList = (title, items, onItemClick) => (
    <Paper variant="outlined" sx={{ width: "100%", minHeight: 160, maxHeight: 200, overflow: "auto" }}>
      <Box sx={{ px: 1, py: 0.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {title} ({items.length})
        </Typography>
      </Box>
      <List dense disablePadding>
        {items.length === 0 ? (
          <ListItem>
            <ListItemText primary={<Typography variant="caption" color="text.disabled">Sin elementos</Typography>} />
          </ListItem>
        ) : (
          items.map((item) => (
            <ListItem
              key={item.id}
              dense
              button
              onClick={() => onItemClick(item)}
              sx={{ py: 0.5, cursor: "pointer" }}
            >
              <ListItemText primary={<Typography variant="body2">{item.label}</Typography>} />
            </ListItem>
          ))
        )}
      </List>
    </Paper>
  )

  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={5}>{customList("Disponibles", disponibles, handleMoveRight)}</Grid>
      <Grid item xs={2}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <Button size="small" variant="outlined" onClick={handleAllRight} disabled={disponibles.length === 0} sx={{ minWidth: 40, px: 0.5, fontSize: 14 }}>≫</Button>
          <Button size="small" variant="outlined" onClick={handleAllLeft} disabled={seleccionados.length === 0} sx={{ minWidth: 40, px: 0.5, fontSize: 14 }}>≪</Button>
        </Box>
      </Grid>
      <Grid item xs={5}>{customList("Seleccionados", seleccionados, handleMoveLeft)}</Grid>
    </Grid>
  )
}

// ----- Confirmation sub-dialog -----
function ConfirmDialog({ open, hayFiltrosActivos, tipo, onConfirm, onCancel }) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: hayFiltrosActivos ? "#ffffff" : "#fffbfb",
          border: hayFiltrosActivos ? undefined : "2px solid #d32f2f",
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {!hayFiltrosActivos && <ReportProblemIcon color="error" />}
        <Typography variant="h6" component="span" color={!hayFiltrosActivos ? "error" : "inherit"}>
          {hayFiltrosActivos ? `Confirmar actualización de ${tipo}` : "¡Atención! Cambio masivo sin filtros"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {hayFiltrosActivos
            ? `Se actualizarán los ${tipo} de los registros que coincidan con los filtros activos. ¿Desea continuar?`
            : `No hay filtros activos. Esta acción afectará TODOS los registros de ${tipo} existentes. ¿Está seguro de que desea continuar?`}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="outlined" size="small">Cancelar</Button>
        <Button onClick={onConfirm} variant="contained" color={hayFiltrosActivos ? "primary" : "error"} size="small" autoFocus>
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ----- Main Modal -----
export default function ModalActualizacionMasiva({
  open,
  handleClose,
  hayFiltrosActivos,
  getClientesConPreciosByFiltros,
  filtros,
  onUpdateCostos,
  onUpdateMargen,
}) {
  // Costos
  const [tipoAjuste, setTipoAjuste] = useState("porcentaje")
  const [valorAjuste, setValorAjuste] = useState("")
  const [loadingCostos, setLoadingCostos] = useState(false)
  const [soloProveedoresReferentesCostos, setSoloProveedoresReferentesCostos] = useState(false)

  // Margen (solo reemplazar)
  const tipoAjusteMargen = "valor"
  const [valorMargen, setValorMargen] = useState("")
  const [loadingMargen, setLoadingMargen] = useState(false)
  const [soloProveedoresReferentesMargen, setSoloProveedoresReferentesMargen] = useState(false)

  // Clientes
  const [actualizarPrecios, setActualizarPrecios] = useState(false)
  const [clientesDisponibles, setClientesDisponibles] = useState([])
  const [clientesSeleccionados, setClientesSeleccionados] = useState([])
  const [loadingClientes, setLoadingClientes] = useState(false)

  // Confirm dialog
  const [confirmData, setConfirmData] = useState({ open: false, tipo: "", callback: null })

  // Result messages
  const [resultMsg, setResultMsg] = useState(null) // { type: "success"|"error", text: "" }

  // Help pop-up: null | "precios" | "costos" | "margen"
  const [helpDialogOpen, setHelpDialogOpen] = useState(null)

  const cargarClientes = useCallback(async () => {
    if (!getClientesConPreciosByFiltros) return
    setLoadingClientes(true)
    try {
      const lista = await getClientesConPreciosByFiltros(filtros)
      setClientesDisponibles(lista || [])
      setClientesSeleccionados([])
    } finally {
      setLoadingClientes(false)
    }
  }, [getClientesConPreciosByFiltros, filtros])

  useEffect(() => {
    if (open) {
      cargarClientes()
      setResultMsg(null)
      setValorAjuste("")
      setValorMargen("")
      setActualizarPrecios(true)
      setClientesSeleccionados([])
      setSoloProveedoresReferentesCostos(false)
      setSoloProveedoresReferentesMargen(false)
    }
  }, [open, cargarClientes])

  const handleTransferChange = (nuevosDisponibles, nuevosSeleccionados) => {
    setClientesDisponibles(nuevosDisponibles)
    setClientesSeleccionados(nuevosSeleccionados)
  }

  const askConfirm = (tipo, callback) => {
    setConfirmData({ open: true, tipo, callback })
  }

  const handleConfirm = async () => {
    setConfirmData((prev) => ({ ...prev, open: false }))
    if (confirmData.callback) await confirmData.callback()
  }

  const handleCancelConfirm = () => {
    setConfirmData({ open: false, tipo: "", callback: null })
  }

  const handleAplicarCostos = () => {
    if (!valorAjuste) return
    askConfirm("costos", async () => {
      setLoadingCostos(true)
      setResultMsg(null)
      try {
        const idEntidadesCliente = actualizarPrecios ? clientesSeleccionados.map((c) => c.id) : []
        const result = await onUpdateCostos(
          tipoAjuste,
          Number.parseFloat(valorAjuste),
          actualizarPrecios && idEntidadesCliente.length > 0,
          idEntidadesCliente,
          soloProveedoresReferentesCostos,
        )
        setResultMsg({ type: "success", text: result?.message || "Costos actualizados correctamente." })
        setValorAjuste("")
      } catch (err) {
        const msg = err?.response?.data?.message || "Error al actualizar costos."
        setResultMsg({ type: "error", text: msg })
      } finally {
        setLoadingCostos(false)
      }
    })
  }

  const handleAplicarMargen = () => {
    if (!valorMargen) return
    askConfirm("margen", async () => {
      setLoadingMargen(true)
      setResultMsg(null)
      try {
        const idEntidadesCliente = actualizarPrecios ? clientesSeleccionados.map((c) => c.id) : []
        const result = await onUpdateMargen(
          Number.parseFloat(valorMargen),
          tipoAjusteMargen,
          actualizarPrecios && idEntidadesCliente.length > 0,
          idEntidadesCliente,
          soloProveedoresReferentesMargen,
        )
        setResultMsg({ type: "success", text: result?.message || "Margen de ganancia actualizado correctamente." })
        setValorMargen("")
      } catch (err) {
        const msg = err?.response?.data?.message || "Error al actualizar margen."
        setResultMsg({ type: "error", text: msg })
      } finally {
        setLoadingMargen(false)
      }
    })
  }

  const isLoading = loadingCostos || loadingMargen

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: "92vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PriceChangeIcon color="secondary" />
            <Typography variant="h6" component="span">
              Actualización masiva de costos y márgenes
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>
          {resultMsg && (
            <Alert severity={resultMsg.type} onClose={() => setResultMsg(null)}>
              {resultMsg.text}
            </Alert>
          )}

          {!hayFiltrosActivos && (
            <Alert severity="warning" icon={<ReportProblemIcon />}>
              No hay filtros activos. Las actualizaciones afectarán <strong>todos</strong> los registros existentes.
            </Alert>
          )}

          {/* Sección precios de clientes - ARRIBA */}
          <Divider textAlign="left">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                ACTUALIZACIÓN DE PRECIOS DE CLIENTES
              </Typography>
              <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setHelpDialogOpen("precios")}>
                <HelpOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </IconButton>
            </Box>
          </Divider>

          <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={actualizarPrecios}
                    onChange={(e) => setActualizarPrecios(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={500}>
                    Actualizar también precios de clientes
                  </Typography>
                }
              />
              {loadingClientes && <CircularProgress size={16} />}
              {!loadingClientes && (
                <Button size="small" variant="text" onClick={cargarClientes} sx={{ textTransform: "none", fontSize: 12 }}>
                  Recargar clientes
                </Button>
              )}
            </Box>

            {actualizarPrecios && (
              <>
                {clientesDisponibles.length === 0 && clientesSeleccionados.length === 0 && !loadingClientes ? (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    No se encontraron clientes con precios asociados a los filtros activos.
                  </Alert>
                ) : (
                  <TransferList
                    disponibles={clientesDisponibles}
                    seleccionados={clientesSeleccionados}
                    onChange={handleTransferChange}
                  />
                )}
                {actualizarPrecios && clientesSeleccionados.length === 0 && (
                  <Typography variant="caption" color="warning.main">
                    Seleccioná al menos un cliente para actualizar sus precios.
                  </Typography>
                )}
              </>
            )}
          </Paper>

          {/* Sección Costos + Margen - ABAJO */}
          <Grid container spacing={2} alignItems="stretch">
            {/* Costos */}
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  borderColor: "secondary.light",
                  backgroundColor: "rgba(156,39,176,0.02)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "secondary.main" }}>
                    Actualizar Costos
                  </Typography>
                  <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setHelpDialogOpen("costos")}>
                    <HelpOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </IconButton>
                </Box>

                <FormControl size="small" fullWidth>
                  <InputLabel>Tipo de ajuste</InputLabel>
                  <Select
                    value={tipoAjuste}
                    onChange={(e) => setTipoAjuste(e.target.value)}
                    label="Tipo de ajuste"
                    startAdornment={
                      <InputAdornment position="start">
                        {tipoAjuste === "porcentaje" ? <PercentIcon fontSize="small" color="action" /> : <AttachMoneyIcon fontSize="small" color="action" />}
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="porcentaje">Porcentaje (%)</MenuItem>
                    <MenuItem value="valor">Valor fijo ($)</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  fullWidth
                  label={tipoAjuste === "porcentaje" ? "Porcentaje" : "Valor"}
                  type="number"
                  value={valorAjuste}
                  onChange={(e) => setValorAjuste(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">{tipoAjuste === "porcentaje" ? "%" : "$"}</InputAdornment>
                    ),
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={soloProveedoresReferentesCostos}
                      onChange={(e) => setSoloProveedoresReferentesCostos(e.target.checked)}
                      color="secondary"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Solo proveedores referentes del filtro
                    </Typography>
                  }
                />

                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={loadingCostos ? <CircularProgress size={16} color="inherit" /> : <PriceChangeIcon />}
                  disabled={isLoading || !valorAjuste}
                  onClick={handleAplicarCostos}
                  sx={{ textTransform: "none", mt: "auto" }}
                >
                  Aplicar costos a filtrados
                </Button>
              </Paper>
            </Grid>

            {/* Margen */}
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  borderColor: "success.light",
                  backgroundColor: "rgba(76,175,80,0.02)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "success.dark" }}>
                    Actualizar Margen %
                  </Typography>
                  <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setHelpDialogOpen("margen")}>
                    <HelpOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </IconButton>
                </Box>

                <TextField
                  size="small"
                  fullWidth
                  label="Nuevo margen (%)"
                  type="number"
                  value={valorMargen}
                  onChange={(e) => setValorMargen(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">%</InputAdornment>,
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={soloProveedoresReferentesMargen}
                      onChange={(e) => setSoloProveedoresReferentesMargen(e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Solo proveedores referentes del filtro
                    </Typography>
                  }
                />

                <Button
                  variant="contained"
                  startIcon={loadingMargen ? <CircularProgress size={16} color="inherit" /> : <PercentIcon />}
                  disabled={isLoading || !valorMargen}
                  onClick={handleAplicarMargen}
                  sx={{
                    textTransform: "none",
                    mt: "auto",
                    backgroundColor: "#4caf50",
                    "&:hover": { backgroundColor: "#388e3c" },
                  }}
                >
                  Aplicar margen a filtrados
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={handleClose} variant="outlined" sx={{ textTransform: "none" }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={helpDialogOpen !== null}
        onClose={() => setHelpDialogOpen(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid",
            borderColor: "grey.200",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 2,
            px: 2.5,
            bgcolor: "grey.100",
            borderBottom: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: "grey.200",
              color: "grey.700",
            }}
          >
            {helpDialogOpen === "precios" && <PriceChangeIcon fontSize="small" />}
            {helpDialogOpen === "costos" && <AttachMoneyIcon fontSize="small" />}
            {helpDialogOpen === "margen" && <PercentIcon fontSize="small" />}
          </Box>
          <Typography variant="h6" component="span" fontWeight={600} color="grey.800">
            {helpDialogOpen === "precios" && "¿Cómo funciona Actualizar Precios de Clientes?"}
            {helpDialogOpen === "costos" && "¿Cómo funciona Actualizar Costos?"}
            {helpDialogOpen === "margen" && "¿Cómo funciona Actualizar Margen %?"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 2.5, bgcolor: "grey.50" }}>
          {helpDialogOpen === "precios" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="body2" color="grey.700">
                Al activar esta opción, al aplicar costos o márgenes, los precios de venta se recalculan automáticamente.
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  borderColor: "grey.200",
                  bgcolor: "grey.100",
                }}
              >
                <Typography variant="caption" color="grey.600" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>
                  Ejemplo
                </Typography>
                <Typography variant="body2" color="grey.800" component="span">
                  Si actualizás el costo de un producto a{" "}
                  <Box component="span" sx={{ color: "warning.main", fontWeight: 600 }}>$100</Box>
                  {" "}y el margen a{" "}
                  <Box component="span" sx={{ color: "warning.main", fontWeight: 600 }}>25%</Box>
                  , el precio de venta pasará a ser{" "}
                  <Box component="span" sx={{ color: "success.main", fontWeight: 600 }}>$125</Box>.
                </Typography>
              </Paper>
              <Typography variant="body2" color="grey.700">
                Solo se actualizan productos con proveedor referente y margen definido.
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  borderColor: "grey.200",
                  bgcolor: "grey.100",
                }}
              >
                <Typography variant="caption" color="grey.600" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>
                  Uso de la lista
                </Typography>
                <Typography variant="body2" color="grey.800">
                  Mové los clientes que querés actualizar desde "Disponibles" a "Seleccionados" usando los botones o haciendo clic en cada cliente. Solo se actualizarán los precios de esos clientes específicos que selecciones.
                </Typography>
              </Paper>
            </Box>
          )}
          {helpDialogOpen === "costos" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  borderColor: "grey.200",
                  bgcolor: "grey.100",
                }}
              >
                <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "warning.main", fontWeight: 600 }}>
                  Porcentaje
                </Typography>
                <Typography variant="body2" color="grey.700" sx={{ mb: 1 }}>
                  Aumenta o reduce el costo actual según el % que ingreses.
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.5 } }}>
                  <Typography component="li" variant="body2" color="grey.800">
                    <Box component="span" sx={{ color: "warning.main", fontWeight: 500 }}>10% sube $100</Box>
                    {" → "}
                    <Box component="span" sx={{ color: "success.main", fontWeight: 600 }}>$110</Box>
                  </Typography>
                  <Typography component="li" variant="body2" color="grey.800">
                    <Box component="span" sx={{ color: "warning.main", fontWeight: 500 }}>-5% baja $200</Box>
                    {" → "}
                    <Box component="span" sx={{ color: "success.main", fontWeight: 600 }}>$190</Box>
                  </Typography>
                </Box>
              </Paper>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  borderColor: "grey.200",
                  bgcolor: "grey.100",
                }}
              >
                <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "warning.main", fontWeight: 600 }}>
                  Valor fijo
                </Typography>
                <Typography variant="body2" color="grey.700" sx={{ mb: 0.5 }}>
                  Reemplaza el costo actual por el valor ingresado.
                </Typography>
                <Typography variant="body2" color="grey.800" fontWeight={500} component="span">
                  Ejemplo: Si ingresás{" "}
                  <Box component="span" sx={{ color: "warning.main", fontWeight: 600 }}>500</Box>
                  , todos los costos filtrados pasan a ser{" "}
                  <Box component="span" sx={{ color: "success.main", fontWeight: 600 }}>$500</Box>.
                </Typography>
              </Paper>
            </Box>
          )}
          {helpDialogOpen === "margen" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="body2" color="grey.700">
                Reemplaza el margen actual por el valor ingresado.
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  borderColor: "grey.200",
                  bgcolor: "grey.100",
                }}
              >
                <Typography variant="caption" color="grey.600" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>
                  Ejemplo
                </Typography>
                <Typography variant="body2" color="grey.800" component="span">
                  Si ingresás{" "}
                  <Box component="span" sx={{ color: "warning.main", fontWeight: 600 }}>25</Box>
                  , todos los productos filtrados tendrán 25% de margen. Un costo de{" "}
                  <Box component="span" sx={{ color: "warning.main", fontWeight: 600 }}>$100</Box>
                  {" "}tendrá precio de venta{" "}
                  <Box component="span" sx={{ color: "success.main", fontWeight: 600 }}>$125</Box>.
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2, bgcolor: "grey.100", borderTop: "1px solid", borderColor: "grey.200" }}>
          <Button
            onClick={() => setHelpDialogOpen(null)}
            variant="contained"
            sx={{
              textTransform: "none",
              px: 3,
              borderRadius: 1.5,
              bgcolor: "grey.700",
              "&:hover": { bgcolor: "grey.800" },
            }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmData.open}
        hayFiltrosActivos={hayFiltrosActivos}
        tipo={confirmData.tipo}
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
      />
    </>
  )
}
